/**
 * PIQ 8.3.2: Robust Extractors for Exec Brief Metrics
 * =====================================================
 * Handles various data shapes from the API for requiredMetrics.rows
 * without fabricating values - only extracts and formats what exists.
 */

// ============================================================
// Core Type Helpers
// ============================================================

/**
 * Safely extract a number from various input types
 */
export function getNum(x: unknown): number | null {
  if (x === null || x === undefined || x === '') return null;
  if (typeof x === 'number') return isNaN(x) ? null : x;
  if (typeof x === 'string') {
    // Strip currency symbols and commas for parsing
    const cleaned = x.replace(/[$,€£¥]/g, '').trim();
    if (cleaned === '' || cleaned === '—' || cleaned === '-') return null;
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  }
  return null;
}

/**
 * Safely extract a string from various input types
 */
export function getStr(x: unknown): string | null {
  if (x === null || x === undefined) return null;
  if (typeof x === 'string') return x.trim() || null;
  if (typeof x === 'number') return String(x);
  return null;
}

// ============================================================
// Revenue Extractors
// ============================================================

/**
 * Extract revenue value from various row shapes:
 * - row.revenue?.value
 * - row.revenueValue
 * - row.revenue (if numeric)
 * - row.metrics?.revenue?.value
 * - parseFloat(row.revenueFormatted) only if numeric string
 */
export function extractRevenue(row: any): number | null {
  if (!row) return null;
  
  // Try structured paths first
  const candidates = [
    row.revenue?.value,
    row.revenueValue,
    typeof row.revenue === 'number' ? row.revenue : null,
    row.metrics?.revenue?.value,
    row.metrics?.revenue,
    row.value,
  ];
  
  for (const candidate of candidates) {
    const num = getNum(candidate);
    if (num !== null) return num;
  }
  
  // Last resort: try to parse formatted string (only if clearly numeric)
  const formatted = row.revenue?.formatted || row.revenueFormatted;
  if (formatted && typeof formatted === 'string') {
    // Only parse if it looks like a currency number
    if (/^[\$€£¥]?[\d,]+\.?\d*$/.test(formatted.trim())) {
      const num = getNum(formatted);
      if (num !== null) return num;
    }
  }
  
  return null;
}

/**
 * Extract formatted revenue string:
 * - row.revenue?.formatted
 * - row.revenueFormatted
 * - Format from numeric value if available
 * - "—" as fallback
 */
export function extractRevenueFormatted(row: any): string {
  if (!row) return '—';
  
  // Try direct formatted values
  const directFormatted = row.revenue?.formatted || row.revenueFormatted;
  if (directFormatted && typeof directFormatted === 'string' && directFormatted !== '—') {
    return directFormatted;
  }
  
  // Try to format from numeric
  const numValue = extractRevenue(row);
  if (numValue !== null) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  }
  
  return '—';
}

// ============================================================
// WoW (Week-over-Week) Extractors
// ============================================================

/**
 * Extract WoW percentage value from various row shapes:
 * - row.wow?.value
 * - row.wowValue
 * - row.wowPct
 * - row.wow (if numeric)
 * - row.metrics?.wow?.value
 * - row.weekOverWeek?.value
 */
export function extractWowPct(row: any): number | null {
  if (!row) return null;
  
  const candidates = [
    row.wow?.value,
    row.wowValue,
    row.wowPct,
    typeof row.wow === 'number' ? row.wow : null,
    row.metrics?.wow?.value,
    row.metrics?.wow,
    row.weekOverWeek?.value,
    row.weekOverWeek,
  ];
  
  for (const candidate of candidates) {
    const num = getNum(candidate);
    if (num !== null) return num;
  }
  
  return null;
}

/**
 * Extract formatted WoW string:
 * - row.wow?.formatted
 * - row.wowFormatted
 * - Format from numeric value if available
 * - "—" as fallback
 */
export function extractWowFormatted(row: any): string {
  if (!row) return '—';
  
  const directFormatted = row.wow?.formatted || row.wowFormatted;
  if (directFormatted && typeof directFormatted === 'string' && directFormatted !== '—') {
    return directFormatted;
  }
  
  const numValue = extractWowPct(row);
  if (numValue !== null) {
    const sign = numValue >= 0 ? '+' : '';
    return `${sign}${numValue.toFixed(1)}%`;
  }
  
  return '—';
}

// ============================================================
// YoY (Year-over-Year) Extractors
// ============================================================

/**
 * Extract YoY percentage value from various row shapes:
 * - row.yoy?.value
 * - row.yoyValue
 * - row.yoyPct
 * - row.yoy (if numeric)
 * - row.metrics?.yoy?.value
 * - row.yearOverYear?.value
 */
export function extractYoyPct(row: any): number | null {
  if (!row) return null;
  
  const candidates = [
    row.yoy?.value,
    row.yoyValue,
    row.yoyPct,
    typeof row.yoy === 'number' ? row.yoy : null,
    row.metrics?.yoy?.value,
    row.metrics?.yoy,
    row.yearOverYear?.value,
    row.yearOverYear,
  ];
  
  for (const candidate of candidates) {
    const num = getNum(candidate);
    if (num !== null) return num;
  }
  
  return null;
}

/**
 * Extract formatted YoY string:
 * - row.yoy?.formatted
 * - row.yoyFormatted
 * - Format from numeric value if available
 * - "—" as fallback
 */
export function extractYoyFormatted(row: any): string {
  if (!row) return '—';
  
  const directFormatted = row.yoy?.formatted || row.yoyFormatted;
  if (directFormatted && typeof directFormatted === 'string' && directFormatted !== '—') {
    return directFormatted;
  }
  
  const numValue = extractYoyPct(row);
  if (numValue !== null) {
    const sign = numValue >= 0 ? '+' : '';
    return `${sign}${numValue.toFixed(1)}%`;
  }
  
  return '—';
}

// ============================================================
// Channel Extractor
// ============================================================

/**
 * Extract channel name from row:
 * - row.channel
 * - row.channelName
 * - row.name
 * - row.label
 * - "Unknown" as fallback
 */
export function extractChannel(row: any): string {
  if (!row) return 'Unknown';
  
  const candidates = [row.channel, row.channelName, row.name, row.label];
  for (const candidate of candidates) {
    const str = getStr(candidate);
    if (str) return str;
  }
  
  return 'Unknown';
}

/**
 * Generate channel key for React key/lookup
 */
export function extractChannelKey(row: any): string {
  const channel = extractChannel(row);
  return channel.toLowerCase().replace(/[^a-z0-9]/g, '_');
}

// ============================================================
// Full Row Extractor
// ============================================================

export interface ExtractedChannelMetric {
  channel: string;
  channelKey: string;
  revenue: number | null;
  revenueFormatted: string;
  wowPct: number | null;
  wowFormatted: string;
  yoyPct: number | null;
  yoyFormatted: string;
  hasConflict: boolean;
  evidenceRefs: string[];
}

/**
 * Extract all metrics from a row
 */
export function extractChannelMetric(row: any): ExtractedChannelMetric {
  return {
    channel: extractChannel(row),
    channelKey: extractChannelKey(row),
    revenue: extractRevenue(row),
    revenueFormatted: extractRevenueFormatted(row),
    wowPct: extractWowPct(row),
    wowFormatted: extractWowFormatted(row),
    yoyPct: extractYoyPct(row),
    yoyFormatted: extractYoyFormatted(row),
    hasConflict: Boolean(row.hasConflict || row.conflict || row.dataConflict),
    evidenceRefs: Array.isArray(row.evidenceRefs) ? row.evidenceRefs : [],
  };
}

/**
 * Extract all channel metrics from requiredMetrics.rows
 */
export function extractAllChannelMetrics(rows: any[] | undefined | null): ExtractedChannelMetric[] {
  if (!rows || !Array.isArray(rows)) return [];
  return rows.map(extractChannelMetric);
}
